import React, { useEffect, useState } from "react";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, TextField } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { FiEdit, FiPlus, FiTrash2, FiX } from "react-icons/fi";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  createCompleteYourDaySet,
  deleteCompleteYourDaySet,
  fetchAvailableCompleteYourDayThings,
  fetchCompleteYourDaySets,
  updateCompleteYourDaySet,
} from "../../../services/adminRestapiService";
import TopBar from "../../common/TopBar";
import Sidebar from "../../common/Sidebar";
import { getImageUploadError, IMAGE_UPLOAD_ACCEPT, IMAGE_UPLOAD_HELP } from "../../../utils/imageUpload";

const API_URL = "https://api.gosehari.com";
const TIME_PERIODS = ["Morning", "Afternoon", "Evening", "Night"];
const emptyImageSlots = () => Array(TIME_PERIODS.length).fill(null);
const emptyDayPlanSlots = () => TIME_PERIODS.map((period) => ({ period_title: period, description: "", image_url: null }));
const emptyDayPlan = () => ({ thing_id: "", slots: emptyDayPlanSlots() });
const getImageUrl = (imagePath) => imagePath?.startsWith("http") || imagePath?.startsWith("blob:") ? imagePath : `${API_URL}${imagePath}`;

function CompleteYourDay() {
  const [dayPlanSets, setDayPlanSets] = useState([]);
  const [availableThings, setAvailableThings] = useState([]);
  const [canAdd, setCanAdd] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSet, setEditingSet] = useState(null);
  const [form, setForm] = useState(emptyDayPlan);
  const [imageFiles, setImageFiles] = useState(emptyImageSlots);
  const [saving, setSaving] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const token = localStorage.getItem("authToken") || localStorage.getItem("token");

  const loadDayPlanSets = async () => {
    setLoading(true);
    try {
      setDayPlanSets((await fetchCompleteYourDaySets(token)).data.data || []);
    } catch {
      toast.error("Failed to load Complete Your Day");
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableThings = async () => {
    try {
      setAvailableThings((await fetchAvailableCompleteYourDayThings(token)).data.data || []);
    } catch {
      setAvailableThings([]);
      toast.error("Failed to load available Top Things");
    }
  };

  const refreshAddAvailability = async () => {
    try {
      const things = (await fetchAvailableCompleteYourDayThings(token)).data.data || [];
      setCanAdd(things.some((thing) => !thing.is_mapped));
    } catch {
      setCanAdd(false);
    }
  };

  useEffect(() => {
    loadDayPlanSets();
    refreshAddAvailability();
  }, []);

  const openAdd = async () => {
    await loadAvailableThings();
    setEditingSet(null);
    setForm(emptyDayPlan());
    setImageFiles(emptyImageSlots());
    setDialogOpen(true);
  };

  const openEdit = async (dayPlanSet) => {
    await loadAvailableThings();
    setEditingSet(dayPlanSet);
    setForm({
      thing_id: String(dayPlanSet.thing_id),
      slots: Array.from({ length: TIME_PERIODS.length }, (_, index) => ({
        period_title: dayPlanSet.slots?.[index]?.period_title || TIME_PERIODS[index],
        description: dayPlanSet.slots?.[index]?.description || "",
        image_url: dayPlanSet.slots?.[index]?.image_url || null,
      })),
    });
    setImageFiles(emptyImageSlots());
    setDialogOpen(true);
  };

  const clearImageSlot = (index) => {
    setForm((current) => ({
      ...current,
      slots: current.slots.map((slot, slotIndex) => slotIndex === index ? { ...slot, image_url: null } : slot),
    }));
    setImageFiles((current) => current.map((file, fileIndex) => fileIndex === index ? null : file));
  };

  const selectImage = (index, file, input) => {
    const imageError = getImageUploadError(file);
    if (imageError) {
      toast.error(imageError);
      input.value = "";
      return;
    }
    setImageFiles((current) => current.map((currentFile, fileIndex) => fileIndex === index ? (file || null) : currentFile));
  };

  const updateSlot = (index, field, value) => {
    setForm((current) => ({
      ...current,
      slots: current.slots.map((slot, slotIndex) => slotIndex === index ? { ...slot, [field]: value } : slot),
    }));
  };

  const submit = async () => {
    if (!form.thing_id) return toast.error("Select a Top Thing");
    if (!form.slots.some((slot) => slot.image_url) && !imageFiles.some(Boolean)) return toast.error("Upload at least one time-period image");

    setSaving(true);
    try {
      const payload = new FormData();
      payload.append("thing_id", form.thing_id);
      payload.append("existing_images", JSON.stringify(form.slots.map((slot) => slot.image_url)));
      payload.append("slot_details", JSON.stringify(form.slots.map(({ period_title, description }) => ({ period_title, description }))));
      payload.append("slot_indexes", JSON.stringify(imageFiles.map((file, index) => file ? index : null).filter((index) => index !== null)));
      imageFiles.forEach((file) => { if (file) payload.append("images", file); });
      if (editingSet) await updateCompleteYourDaySet(token, editingSet.thing_id, payload);
      else await createCompleteYourDaySet(token, payload);
      toast.success(editingSet ? "Complete Your Day updated" : "Complete Your Day created");
      setDialogOpen(false);
      await Promise.all([loadDayPlanSets(), refreshAddAvailability()]);
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to save Complete Your Day");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (dayPlanSet) => {
    if (!window.confirm(`Delete every Complete Your Day image for “${dayPlanSet.thing_title}”?`)) return;
    try {
      await deleteCompleteYourDaySet(token, dayPlanSet.thing_id);
      toast.success("Complete Your Day deleted; its Top Thing is available again");
      await Promise.all([loadDayPlanSets(), refreshAddAvailability()]);
    } catch (error) {
      toast.error(error.response?.data?.message || "Unable to delete Complete Your Day");
    }
  };

  const columns = [
    { field: "thing_id", headerName: "Top Thing ID", width: 105 },
    { field: "thing_title", headerName: "Top Thing", flex: 1.3, minWidth: 200 },
    { field: "thing_category", headerName: "Category", width: 140 },
    {
      field: "slots", headerName: "Time-period images", width: 435, sortable: false,
      renderCell: ({ value = [], row }) => <div className="flex h-full items-center gap-1">
        {TIME_PERIODS.map((period, index) => value[index]?.image_url ? (
          <div key={period} className="relative"><img src={getImageUrl(value[index].image_url)} alt={`${row.thing_title} ${period}`} className="h-14 w-20 rounded-lg border border-slate-200 object-cover" /><span className="absolute inset-x-0 bottom-0 rounded-b-lg bg-slate-900/65 py-0.5 text-center text-[9px] text-white">{period}</span></div>
        ) : <div key={period} className="flex h-14 w-20 items-center justify-center rounded-lg border border-dashed border-slate-300 bg-slate-50 px-1 text-center text-[10px] text-slate-400">{period}</div>)}
      </div>,
    },
    {
      field: "actions", headerName: "Actions", width: 125, sortable: false,
      renderCell: ({ row }) => <Box><IconButton aria-label="Edit Complete Your Day" color="primary" onClick={() => openEdit(row)}><FiEdit /></IconButton><IconButton aria-label="Delete Complete Your Day" color="error" onClick={() => remove(row)}><FiTrash2 /></IconButton></Box>,
    },
  ];

  const hasAvailableThings = availableThings.some((thing) => !thing.is_mapped);
  const selectedTopThing = availableThings.find((thing) => String(thing.id) === String(form.thing_id));

  return (
    <div className="flex min-h-screen bg-slate-50">
      <ToastContainer position="top-right" autoClose={3000} theme="colored" />
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className={`min-h-screen min-w-0 flex-1 transition-[margin] duration-300 ${sidebarOpen ? "lg:ml-64" : "lg:ml-20"}`}>
        <TopBar toggleSidebar={() => setSidebarOpen((open) => !open)} sidebarOpen={sidebarOpen} />
        <main className="space-y-6 px-4 pb-8 pt-24 sm:px-6 lg:px-8">
          <section className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm sm:p-5">
            <div className="mb-5 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div><h1 className="text-xl font-semibold text-slate-900 sm:text-2xl">Complete Your Day</h1><p className="mt-1 text-sm text-slate-600">Manage fixed Morning, Afternoon, Evening, and Night image slots for each Top Thing.</p>{!loading && !canAdd && <p className="mt-1 text-sm text-amber-700">All Top Things already have Complete Your Day images.</p>}</div>
              <Button variant="contained" startIcon={<FiPlus />} onClick={openAdd} disabled={loading || !canAdd} className="self-start sm:self-auto">Add Complete Your Day</Button>
            </div>
            <div className="min-w-0 overflow-x-auto rounded-xl border border-slate-100"><DataGrid rows={dayPlanSets} columns={columns} loading={loading} autoHeight rowHeight={68} disableRowSelectionOnClick pageSizeOptions={[10, 25, 50]} initialState={{ pagination: { paginationModel: { pageSize: 10 } } }} sx={{ minWidth: 1015, border: 0 }} /></div>
          </section>
        </main>
      </div>

      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>{editingSet ? "Edit Complete Your Day" : "Add Complete Your Day"}</DialogTitle>
        <DialogContent>
          {!editingSet && !hasAvailableThings ? <p className="py-4 text-slate-700">All Top Things already have Complete Your Day images.</p> : (
            <Box component="form" className="flex flex-col gap-4 pt-3">
              {editingSet ? <div><span className="mb-1 block text-sm">Top Thing</span><div className="w-full rounded-lg border border-slate-200 bg-slate-50 p-3 text-slate-700">{editingSet.thing_title}</div></div> : (
                <label><span className="mb-1 block text-sm">Top Thing *</span><select name="thing_id" value={form.thing_id} onChange={(event) => setForm((current) => ({ ...current, thing_id: event.target.value }))} className="w-full rounded-lg border border-slate-300 p-3" required><option value="">Select a Top Thing</option>{availableThings.map((thing) => <option key={thing.id} value={thing.id} disabled={thing.is_mapped}>{thing.title} ({thing.category}){thing.is_mapped ? " — Complete Your Day already added" : ""}</option>)}</select></label>
              )}
              {selectedTopThing && !editingSet && <div className="rounded-lg border border-slate-200 bg-slate-50 p-3"><p className="font-semibold text-slate-800">{selectedTopThing.title}</p><p className="mt-1 text-sm text-slate-500">{selectedTopThing.category}</p><p className="mt-1 text-sm text-slate-600">{selectedTopThing.description || "No description available."}</p></div>}
              <div><p className="mb-2 text-sm">Time-period images</p><div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {TIME_PERIODS.map((period, index) => {
                  const slot = form.slots[index];
                  const preview = imageFiles[index] ? URL.createObjectURL(imageFiles[index]) : slot.image_url;
                  return <div key={period} className="rounded-lg border border-slate-200 p-2 text-sm"><div className="mb-1 flex items-center justify-between"><span>{period}</span>{preview && <button type="button" aria-label={`Remove ${period} image`} onClick={() => clearImageSlot(index)} className="rounded p-1 text-slate-500 transition hover:bg-rose-50 hover:text-rose-600"><FiX /></button>}</div>{preview ? <img src={getImageUrl(preview)} alt={`${period} preview`} className="h-28 w-full rounded-md object-cover" /> : <div className="flex h-28 items-center justify-center rounded-md border border-dashed border-slate-300 bg-slate-50 text-slate-400">Empty {period} image</div>}<input className="mt-2 block w-full" type="file" accept={IMAGE_UPLOAD_ACCEPT} onChange={(event) => selectImage(index, event.target.files[0], event.target)} /><TextField className="mt-2" label={`${period} title`} value={slot.period_title} onChange={(event) => updateSlot(index, "period_title", event.target.value)} fullWidth size="small" /><TextField className="mt-2" label={`${period} description`} value={slot.description} onChange={(event) => updateSlot(index, "description", event.target.value)} multiline minRows={2} fullWidth size="small" /></div>;
                })}




              </div>

              
              
              </div>
              <p className="text-xs text-slate-500">Each image is displayed in its fixed time period: Morning, Afternoon, Evening, then Night. {IMAGE_UPLOAD_HELP}</p>
            </Box>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>{(editingSet || hasAvailableThings) && <Button variant="contained" onClick={submit} disabled={saving}>{saving ? "Saving…" : "Save"}</Button>}</DialogActions>
      </Dialog>
    </div>
  );
}

export default CompleteYourDay;
